import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Container } from '@mui/material';
import ImageList from '../components/images/ImageList';
import ImageManager from '../components/images/ImageManager';
import { Image } from './../types';
import { useLang } from '../contexts/langContext';
import { getPublicImages } from '../services/images';
import { useAppContext } from '../contexts/AppContext';
import TagFilterChips from '../components/common/TagFilterChips';
import { useTags } from '../hooks/useTags';
import { useSearchParams } from 'react-router-dom';

function ImagePage() {
  const { lang } = useLang();
  const [tab, setTab] = useState(0);
  const [publicImages, setPublicImages] = useState<Image[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTagsParam = searchParams.get('tags');
  const initialSelectedTagIds = initialTagsParam ? initialTagsParam.split(',').filter(Boolean) : [];
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialSelectedTagIds);

  const { tags } = useTags();
  const [tagCounts, setTagCounts] = useState<Record<string | 'all', number> | undefined>(undefined);
  const [myImagesCounts, setMyImagesCounts] = useState<Record<string | 'all', number> | undefined>(undefined);

  const { setAppTitle } = useAppContext();

  useEffect(() => {
    setAppTitle(lang === 'en' ? 'Image Gallery' : '图库');
  }, []);

  useEffect(() => {
    if (tab === 1) {
      fetchPublicImages();
    }
  }, [tab, selectedTagIds]);

  // Calculate tag counts for public images
  useEffect(() => {
    if (tab === 1 && publicImages.length >= 0) {
      const counts: Record<string | 'all', number> = { all: publicImages.length };
      publicImages.forEach(image => {
        (image.tagIds || []).forEach(tagId => {
          counts[tagId] = (counts[tagId] || 0) + 1;
        });
      });
      setTagCounts(counts);
    }
  }, [publicImages, tab]);

  // Update overall tag counts when switching tabs or when my images counts change
  useEffect(() => {
    if (tab === 0 && myImagesCounts) {
      setTagCounts(myImagesCounts);
    }
  }, [tab, myImagesCounts]);

  const handleMyImagesCountsChange = (counts: Record<string | 'all', number>) => {
    setMyImagesCounts(counts);
    if (tab === 0) {
      setTagCounts(counts);
    }
  };

  const fetchPublicImages = async () => {
    const publicImgs = await getPublicImages();
    const filtered = selectedTagIds.length === 0 ? publicImgs : publicImgs.filter(img => selectedTagIds.some(tid => img.tagIds?.includes(tid)));
    setPublicImages(filtered);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Sync tag selection to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedTagIds.length === 0) {
      params.delete('tags');
    } else {
      params.set('tags', selectedTagIds.join(','));
    }
    setSearchParams(params, { replace: true });
  }, [selectedTagIds]);

  return (
    <Container maxWidth="lg">
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab label={lang === 'en' ? 'My Images' : '我的图片'} />
        <Tab label={lang === 'en' ? 'Public Images' : '公共图片'} />
      </Tabs>

      {/* Tag Filter Chips */}
      {tags.length > 0 && (
        <Box sx={{ my: 2 }}>
          <TagFilterChips
            tags={tags}
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
            tagCounts={tagCounts}
          />
        </Box>
      )}

      {tab === 0 && <ImageManager selectedTagIds={selectedTagIds} onCountsChange={handleMyImagesCountsChange} />}
      {tab === 1 && <ImageList images={publicImages} isUserImages={false} tags={tags} />}
    </Container>
  );
}

export default ImagePage;
